import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalDialogs } from '../contexts/GlobalDialogsContext';
import RenewalCalendar from '../components/RenewalCalendar';
import AIBriefingPanel from '../components/AIBriefingPanel';
import SkipTodayPanel from '../components/SkipTodayPanel';
import HealthScoreCard from '../components/HealthScoreCard';
import TipOfTheDayCard from '../components/TipOfTheDayCard';
import FloatingSymbols from '../components/FloatingSymbols';
import { formatCurrency, formatDate } from '../utils/helpers';
import {
  Users, FileText, Calendar, Target, Lightbulb,
  Heart, Car, Home, Shield, Plus, ChevronDown, ChevronUp, BarChart3,
  Phone, Sparkles, History, TrendingUp, Sun, ArrowRight,
  MessageSquare, Check, FileWarning,
} from 'lucide-react';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_ACTIONS = [
  { label: 'Health', productType: 'Health', icon: Heart,  accent: 'text-rose-600' },
  { label: 'Life',   productType: 'Term',   icon: Shield, accent: 'text-blue-600' },
  { label: 'Motor',  productType: 'Motor',  icon: Car,    accent: 'text-emerald-600' },
  { label: 'Home',   productType: 'Home',   icon: Home,   accent: 'text-amber-600' },
];

const Metric = ({ label, value, tint = 'slate' }) => {
  const tints = {
    slate:  'bg-slate-50 border-slate-200',
    blue:   'bg-blue-50 border-blue-100',
    green:  'bg-emerald-50 border-emerald-100',
    orange: 'bg-orange-50 border-orange-100',
    purple: 'bg-purple-50 border-purple-100',
    red:    'bg-red-50 border-red-100',
    yellow: 'bg-yellow-50 border-yellow-100',
  };
  return (
    <div className={`rounded-lg border ${tints[tint] || tints.slate} px-3 py-2.5`} data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-[22px] font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope' }}>{value}</p>
    </div>
  );
};

const Panel = ({ title, icon: Icon, iconTint = 'text-slate-600', description, children, testId, className = '' }) => (
  <div className={`rounded-xl border border-slate-200 bg-white flex flex-col ${className}`} data-testid={testId}>
    <div className="px-4 pt-3 pb-2 border-b border-slate-100 flex-none">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${iconTint}`} />
        <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>{title}</h3>
      </div>
      {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
    </div>
    <div className="px-4 py-3 flex-1 min-h-0 flex flex-col">{children}</div>
  </div>
);

const PRIORITY_META = {
  1: { label: 'HIGH',   color: 'bg-red-100 text-red-800' },
  2: { label: 'MEDIUM', color: 'bg-amber-100 text-amber-800' },
  3: { label: 'LOW',    color: 'bg-slate-100 text-slate-700' },
};

// Feed-item styling — one look per source so the eye scans the tag column.
const FEED_META = {
  briefing:   { icon: Sun,        border: 'border-l-blue-800',   badge: 'bg-blue-100 text-blue-800',           tag: 'Briefing'  },
  ai:         { icon: Sparkles,   border: 'border-l-purple-600', badge: 'bg-purple-100 text-purple-800',       tag: 'AI'        },
  manual:     { icon: FileText,   border: 'border-l-emerald-500',badge: 'bg-emerald-100 text-emerald-800',     tag: 'Manual'    },
  renewal:    { icon: Calendar,   border: 'border-l-amber-500',  badge: 'bg-amber-100 text-amber-800',         tag: 'Renewal'   },
  opportunity:{ icon: Users,      border: 'border-l-indigo-500', badge: 'bg-indigo-100 text-indigo-800',       tag: 'Cross-Sell'},
  winback:    { icon: History,    border: 'border-l-slate-400',  badge: 'bg-slate-100 text-slate-700',         tag: 'Lost'      },
};

// Feed row — leading icon, title, secondary context, tag on right, colored left border.
// Action buttons (Call / WhatsApp / Complete) are contextually rendered:
//   • briefing         → no actions (pinned summary only)
//   • ai / manual      → Call · WhatsApp · Complete
//   • renewal          → Call · WhatsApp   (renewal is completed via mark-renewed flow)
//   • opportunity      → Call · WhatsApp   (pitch the recommended product)
//   • winback          → Call · WhatsApp   (checkpoint progresses on its own)
const FeedRow = ({ item, busy, onCall, onWhatsApp, onComplete }) => {
  const meta = FEED_META[item.kind] || FEED_META.manual;
  const Icon = meta.icon;
  const showComplete = item.kind === 'ai' || item.kind === 'manual';
  const showContact = item.kind !== 'briefing';

  return (
    <li
      className={`group relative flex items-start gap-3 rounded-md border border-slate-200 border-l-4 ${meta.border} bg-white hover:bg-slate-50 transition p-3`}
      data-testid={`feed-item-${item.kind}-${item.id}`}
    >
      <div className={`shrink-0 h-8 w-8 rounded-md grid place-items-center bg-slate-50 border border-slate-200`}>
        <Icon className="h-4 w-4 text-slate-600" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-900 leading-tight" data-testid={`feed-item-${item.kind}-${item.id}-title`}>
          {item.title}
        </p>
        {item.subtitle && <p className="text-xs text-slate-500 mt-0.5">{item.subtitle}</p>}
        {showContact && (
          <div className="mt-2 flex items-center gap-1.5" data-testid={`feed-item-${item.kind}-${item.id}-actions`}>
            <button
              type="button"
              onClick={() => onCall(item)}
              className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
              disabled={busy}
              data-testid={`feed-action-call-${item.kind}-${item.id}`}
              title="Open device dialer"
            >
              <Phone className="h-3 w-3" /> Call
            </button>
            <button
              type="button"
              onClick={() => onWhatsApp(item)}
              className="inline-flex items-center gap-1 rounded border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-800 hover:bg-green-100 disabled:opacity-50"
              disabled={busy}
              data-testid={`feed-action-whatsapp-${item.kind}-${item.id}`}
              title="Open WhatsApp chat"
            >
              <MessageSquare className="h-3 w-3" /> WhatsApp
            </button>
            {showComplete && (
              <button
                type="button"
                onClick={() => onComplete(item)}
                className="inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                disabled={busy}
                data-testid={`feed-action-complete-${item.kind}-${item.id}`}
                title="Mark this queue item done"
              >
                <Check className="h-3 w-3" /> {busy ? '…' : 'Complete'}
              </button>
            )}
          </div>
        )}
      </div>
      <div className="shrink-0 flex flex-col items-end gap-1">
        <Badge className={`text-[10px] font-medium ${meta.badge}`}>{meta.tag}</Badge>
        {item.value != null && (
          <p className="text-[11px] font-semibold text-emerald-700">{formatCurrency(item.value)}</p>
        )}
      </div>
    </li>
  );
};

const PRIORITY_META_OLD_UNUSED = {
  1: { label: 'Urgent', color: 'bg-red-100 text-red-800' },
  2: { label: 'High',   color: 'bg-amber-100 text-amber-800' },
  3: { label: 'Medium', color: 'bg-blue-100 text-blue-800' },
  4: { label: 'Normal', color: 'bg-slate-100 text-slate-700' },
};

// Unified daily-queue list — AI-suggested + manual tasks, sorted server-side.
// Each row shows a source tag (AI vs manual), priority badge, and a checkbox
// that completes a manual task or dismisses an AI suggestion (for today).
const DailyQueue = ({ queue, onComplete, completingId, compact }) => {
  if (!queue || queue.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-4" data-testid="daily-queue-empty">Nothing on today's queue. Great job!</p>;
  }
  const rows = compact ? queue.slice(0, 5) : queue.slice(0, 12);
  return (
    <ul className={`space-y-2 ${compact ? 'max-h-56' : 'max-h-96'} overflow-y-auto`} data-testid="daily-queue-list">
      {rows.map((item) => {
        const p = PRIORITY_META[item.priority] || PRIORITY_META[3];
        const isAI = item.source === 'ai';
        return (
          <li key={`${item.source}-${item.id}`} className="py-2 border-b border-slate-100 last:border-0 group" data-testid={`daily-queue-item-${item.source}-${item.id}`}>
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => onComplete(item)}
                disabled={completingId === `${item.source}-${item.id}`}
                className="mt-0.5 h-4 w-4 rounded border-2 border-slate-300 hover:border-emerald-600 hover:bg-emerald-50 shrink-0 flex items-center justify-center transition"
                title={isAI ? 'Dismiss this AI suggestion for today' : 'Mark task complete'}
                data-testid={`daily-queue-complete-${item.source}-${item.id}`}
              >
                {completingId === `${item.source}-${item.id}` && <span className="h-2 w-2 rounded-full bg-emerald-600 animate-pulse" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                  <Badge className={`text-[10px] px-1.5 py-0 ${p.color}`} data-testid={`daily-queue-priority-${item.source}-${item.id}`}>{p.label}</Badge>
                  <Badge
                    className={`text-[10px] px-1.5 py-0 ${isAI ? 'bg-purple-100 text-purple-800' : 'bg-emerald-100 text-emerald-800'}`}
                    data-testid={`daily-queue-source-${item.source}-${item.id}`}
                  >
                    {isAI ? 'AI' : 'Manual'}
                  </Badge>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider">{(item.type || '').replace(/_/g, ' ')}</span>
                </div>
                <p className="text-sm font-medium text-slate-900">{item.title}</p>
                {item.reason && <p className="text-xs text-slate-500 mt-0.5">{item.reason}</p>}
              </div>
              {item.revenue_value > 0 && (
                <p className="text-xs font-semibold text-emerald-600 shrink-0">{formatCurrency(item.revenue_value)}</p>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const { openAddPolicy, openAddProduct, openClientLeadPicker, onPolicyCreated } = useGlobalDialogs();
  const [stats, setStats] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [actionPlan, setActionPlan] = useState([]);
  const [dailyQueue, setDailyQueue] = useState([]);
  const [completingId, setCompletingId] = useState('');
  const [opportunities, setOpportunities] = useState([]);
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(true);
  const [narrativeRefreshing, setNarrativeRefreshing] = useState(false);
  const [weeklyWrap, setWeeklyWrap] = useState(null);
  const [weeklyLoading, setWeeklyLoading] = useState(true);
  const [renewals90, setRenewals90] = useState([]);
  const [recentPolicies, setRecentPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiPanelExpanded, setAiPanelExpanded] = useState(false);
  const [glanceOpen, setGlanceOpen] = useState(false);
  const [lostLeads, setLostLeads] = useState([]);
  const [productMix, setProductMix] = useState([]);
  const [claimsAttention, setClaimsAttention] = useState(0);
  const [skipToday, setSkipToday] = useState(null);
  const [skipTodayLoading, setSkipTodayLoading] = useState(true);
  const [healthScore, setHealthScore] = useState(null);
  const [healthScoreLoading, setHealthScoreLoading] = useState(true);
  const [healthTrend, setHealthTrend] = useState(null);
  // Product-to-lead mini-dialog: when the quick-action picker returns a lead
  // (instead of a client) we capture product interest + expected_premium on
  // the lead record itself — no forced client conversion.
  const [leadAttach, setLeadAttach] = useState(null); // { lead_id, name, productType }
  const [leadAttachForm, setLeadAttachForm] = useState({ product: '', expected_premium: '', notes: '' });
  const [leadAttachSaving, setLeadAttachSaving] = useState(false);

  const loadDashboardData = useCallback(async () => {
    try {
      const [statsRes, briefingRes, actionRes, oppRes, renewalsRes, policiesRes, queueRes, lostRes, overviewRes, skipRes, healthRes, trendRes, claimsAttRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/ai/daily-briefing`, { withCredentials: true }),
        axios.get(`${API}/ai/action-plan`, { withCredentials: true }),
        axios.get(`${API}/ai/opportunities`, { withCredentials: true }),
        axios.get(`${API}/renewals?days=90`, { withCredentials: true }),
        axios.get(`${API}/policies`, { withCredentials: true }),
        axios.get(`${API}/daily-queue`, { withCredentials: true }),
        axios.get(`${API}/lost-leads`, { withCredentials: true }),
        axios.get(`${API}/reports/overview`, { withCredentials: true }),
        axios.get(`${API}/ai/skip-today`, { withCredentials: true }),
        axios.get(`${API}/ai/health-score`, { withCredentials: true }),
        axios.get(`${API}/ai/health-score/trend?days=7`, { withCredentials: true }),
        axios.get(`${API}/claims/attention-count`, { withCredentials: true }).catch(() => ({ data: { count: 0 } })),
      ]);
      setStats(statsRes.data);
      setBriefing(briefingRes.data);
      setActionPlan(actionRes.data);
      setOpportunities(oppRes.data);
      setDailyQueue(queueRes.data || []);
      setRenewals90(renewalsRes.data || []);
      setLostLeads((lostRes.data?.rows) || []);
      setProductMix((overviewRes.data?.by_category) || []);
      setSkipToday(skipRes.data);
      setHealthScore(healthRes.data);
      setHealthTrend(trendRes.data);
      setClaimsAttention(claimsAttRes.data?.count || 0);
      const sortedRecent = [...(policiesRes.data || [])]
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, 5);
      setRecentPolicies(sortedRecent);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setSkipTodayLoading(false);
      setHealthScoreLoading(false);
    }
  }, []);

  const handleQueueComplete = useCallback(async (item) => {
    const key = `${item.source}-${item.id}`;
    setCompletingId(key);
    try {
      await axios.post(`${API}/daily-queue/complete`, { source: item.source, id: item.id }, { withCredentials: true });
      // Optimistic remove from local queue so the UI feels instant.
      setDailyQueue((prev) => prev.filter((x) => !(x.source === item.source && x.id === item.id)));
      toast.success(item.source === 'ai' ? 'AI suggestion dismissed for today' : 'Task completed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update queue item');
    } finally { setCompletingId(''); }
  }, []);

  const loadNarrative = useCallback(async (refresh) => {
    if (refresh) setNarrativeRefreshing(true); else setNarrativeLoading(true);
    try {
      const { data } = await axios.get(`${API}/ai/daily-narrative${refresh ? '?refresh=true' : ''}`, { withCredentials: true });
      setNarrative(data);
      if (refresh) toast.success('Fresh briefing generated');
    } catch (error) {
      console.error('Error loading AI narrative:', error);
      setNarrative({ narrative: null, error: error?.response?.data?.detail || 'Narrative unavailable' });
    } finally {
      setNarrativeLoading(false);
      setNarrativeRefreshing(false);
    }
  }, []);

  const loadWeeklyWrap = useCallback(async () => {
    setWeeklyLoading(true);
    try {
      const { data } = await axios.get(`${API}/ai/weekly-wrap`, { withCredentials: true });
      setWeeklyWrap(data);
    } catch (err) {
      console.warn('Weekly wrap load failed:', err?.message);
      setWeeklyWrap(null);
    } finally { setWeeklyLoading(false); }
  }, []);

  useEffect(() => {
    loadDashboardData();
    loadNarrative(false);
    loadWeeklyWrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => onPolicyCreated(() => { loadDashboardData(); }), [onPolicyCreated, loadDashboardData]);

  const runQuickAction = ({ label, productType }) => {
    openClientLeadPicker({
      title: `Add ${label} for…`,
      description: 'Every product must be linked to an existing client or lead before we can save it.',
      onPick: (picked) => {
        if (picked.type === 'client') {
          openAddPolicy({ clientId: picked.client_id, productType });
        } else {
          // Attach product interest directly onto the lead — no client conversion needed.
          setLeadAttach({ lead_id: picked.id, name: picked.name, productType, label });
          setLeadAttachForm({ product: productType, expected_premium: '', notes: '' });
        }
      },
    });
  };

  // Feed row actions ----------------------------------------------------------
  // Phone/name is not attached to every source (opportunities & queue items are
  // "thin"), so we resolve it on click by hitting the right endpoint. Renewal
  // and win-back sources come with the info already.
  const [feedBusyId, setFeedBusyId] = useState('');
  // "Sending…" state for the renewal-calendar side-panel one-tap reminders.
  // Key is `${policy_id}-${channel}` so SMS and WhatsApp on the same row can
  // both run concurrently without one disabling the other.
  const [calSendingId, setCalSendingId] = useState('');

  const sendRenewalReminder = async (r, channel) => {
    if (!r.client_phone) { toast.error('No mobile on file for this client'); return; }
    const key = `${r.policy_id}-${channel}`;
    setCalSendingId(key);
    try {
      // Match the popover's message body so behaviour is identical.
      const dueDate = r.next_due_date ? new Date(r.next_due_date).toLocaleDateString('en-IN') : '';
      const body = `Hi ${r.client_name || 'there'}, this is a reminder that your ${r.product_type || 'policy'} (${r.policy_number || ''}) is due for renewal on ${dueDate}. Please let me know a convenient time to complete it.`;
      await axios.post(`${API}/messages/send`, {
        to: r.client_phone,
        body,
        channel,
        client_id: r.client_id,
      }, { withCredentials: true });
      toast.success(`${channel === 'sms' ? 'SMS' : 'WhatsApp'} sent to ${r.client_name || 'client'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setCalSendingId(''); }
  };

  const resolveContact = async (item) => {
    // Returns { phone, name } or throws.
    if (item.kind === 'renewal') {
      const r = item._raw || renewals90.find((x) => x.policy_id === item.id) || {};
      return { phone: r.client_phone, name: r.client_name, client_id: r.client_id };
    }
    if (item.kind === 'opportunity') {
      const { data } = await axios.get(`${API}/clients/${item.id}`, { withCredentials: true });
      return { phone: data.primary_mobile, name: data.name, client_id: data.client_id };
    }
    if (item.kind === 'winback') {
      const src = lostLeads.find((x) => x.id === item.id);
      if (src?.kind === 'lead') {
        const { data } = await axios.get(`${API}/leads/${item.id}`, { withCredentials: true });
        return { phone: data.phone, name: data.name };
      }
      // Lapsed policy → look up the client for the phone.
      const { data: pol } = await axios.get(`${API}/policies/${item.id}`, { withCredentials: true });
      const { data: cli } = await axios.get(`${API}/clients/${pol.client_id}`, { withCredentials: true });
      return { phone: cli.primary_mobile, name: cli.name, client_id: cli.client_id };
    }
    if (item.kind === 'ai' || item.kind === 'manual') {
      const raw = item._raw || {};
      if (raw.related_to === 'policy' && raw.related_id) {
        const { data: pol } = await axios.get(`${API}/policies/${raw.related_id}`, { withCredentials: true });
        const { data: cli } = await axios.get(`${API}/clients/${pol.client_id}`, { withCredentials: true });
        return { phone: cli.primary_mobile, name: cli.name, client_id: cli.client_id };
      }
      if (raw.related_to === 'client' && raw.related_id) {
        const { data } = await axios.get(`${API}/clients/${raw.related_id}`, { withCredentials: true });
        return { phone: data.primary_mobile, name: data.name, client_id: data.client_id };
      }
      if (raw.related_to === 'lead' && raw.related_id) {
        const { data } = await axios.get(`${API}/leads/${raw.related_id}`, { withCredentials: true });
        return { phone: data.phone, name: data.name };
      }
    }
    throw new Error('No contact linked to this item');
  };

  const normalizePhoneForTel = (p) => {
    if (!p) return '';
    const digits = String(p).replace(/\D/g, '');
    // Assume Indian 10-digit if bare; else keep country prefix.
    return digits.length === 10 ? `+91${digits}` : (digits.startsWith('91') ? `+${digits}` : `+${digits}`);
  };

  const handleFeedCall = async (item) => {
    setFeedBusyId(`${item.kind}-${item.id}`);
    try {
      const { phone } = await resolveContact(item);
      if (!phone) { toast.error('No phone number on file'); return; }
      window.location.href = `tel:${normalizePhoneForTel(phone)}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Could not open dialer');
    } finally { setFeedBusyId(''); }
  };

  const handleFeedWhatsApp = async (item) => {
    setFeedBusyId(`${item.kind}-${item.id}`);
    try {
      const { phone, name } = await resolveContact(item);
      if (!phone) { toast.error('No phone number on file'); return; }
      // Pre-fill message per source so the agent doesn't retype the context.
      let body = `Hi ${name || 'there'}, `;
      if (item.kind === 'renewal') body += `just a quick reminder that your policy is due for renewal. Let me know a good time to complete it.`;
      else if (item.kind === 'winback') body += `it's been a while — happy to help you get covered again at a rate that works.`;
      else if (item.kind === 'opportunity') body += `I noticed a coverage gap worth looking at — want to jump on a quick call?`;
      else body += `following up on your policy. Please share a convenient time.`;
      const digits = String(phone).replace(/\D/g, '');
      const wa = digits.length === 10 ? `91${digits}` : (digits.startsWith('91') ? digits : `91${digits}`);
      window.open(`https://wa.me/${wa}?text=${encodeURIComponent(body)}`, '_blank', 'noopener');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Could not open WhatsApp');
    } finally { setFeedBusyId(''); }
  };

  const handleFeedComplete = async (item) => {
    setFeedBusyId(`${item.kind}-${item.id}`);
    try {
      await axios.post(`${API}/daily-queue/complete`, { id: item.id, source: item.kind }, { withCredentials: true });
      toast.success('Marked complete');
      loadDashboardData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not complete this item');
    } finally { setFeedBusyId(''); }
  };

  const saveLeadAttach = async () => {
    if (!leadAttach) return;
    setLeadAttachSaving(true);
    try {
      await axios.put(`${API}/leads/${leadAttach.lead_id}`, {
        product: leadAttachForm.product || leadAttach.productType,
        expected_premium: leadAttachForm.expected_premium === '' ? null : parseFloat(leadAttachForm.expected_premium),
        notes: leadAttachForm.notes || null,
      }, { withCredentials: true });
      toast.success(`${leadAttach.label} interest attached to "${leadAttach.name}"`, {
        description: 'A policy stub will auto-create when you convert this lead to a client.',
      });
      setLeadAttach(null);
      setLeadAttachForm({ product: '', expected_premium: '', notes: '' });
      loadDashboardData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to attach product');
    } finally { setLeadAttachSaving(false); }
  };
  const priorityFeed = useMemo(() => {
    // Merge every actionable source into one ranked feed. Score is 0-100 (higher = sooner).
    const feed = [];

    // 1. Morning Briefing — always pinned at the very top as a highlighted item.
    const briefingText = String(
      narrative?.narrative || briefing?.summary || briefing?.message || ''
    );
    if (briefingText.trim()) {
      feed.push({
        kind: 'briefing',
        id: 'today',
        title: 'Morning Briefing',
        subtitle: briefingText.replace(/\s+/g, ' ').slice(0, 220),
        score: 200, // pinned above everything
      });
    }

    // 2. Today's Queue (AI + Manual). Priority 1 → score ~90, 2 → 70, 3 → 50.
    dailyQueue.forEach((q) => {
      const base = q.priority === 1 ? 90 : q.priority === 2 ? 70 : 50;
      feed.push({
        kind: q.source === 'ai' ? 'ai' : 'manual',
        id: q.id,
        title: q.title,
        subtitle: q.reason || (q.type || '').replace(/_/g, ' '),
        value: q.revenue_value || null,
        score: base,
        _raw: q, // for complete handler
      });
    });

    // 3. Renewals — anything within 30 days. Sooner = higher score.
    const upcoming = renewals90.filter((r) => (r.days_remaining ?? 999) <= 30);
    upcoming.slice(0, 15).forEach((r) => {
      const dr = r.days_remaining ?? 30;
      const score = 85 - Math.min(dr, 30) * 1.5; // 0d → 85, 30d → 40
      feed.push({
        kind: 'renewal',
        id: r.policy_id,
        title: `Renew ${r.client_name} · ${r.product_type}`,
        subtitle: `Due ${dr === 0 ? 'today' : `in ${dr}d`} · ${r.policy_number || '—'}`,
        value: r.premium_amount,
        score,
      });
    });

    // 4. Cross-Sell Opportunities.
    opportunities.slice(0, 8).forEach((o) => {
      feed.push({
        kind: 'opportunity',
        id: o.client_id,
        title: `Cross-sell to ${o.client_name}`,
        subtitle: (o.recommended_products || []).slice(0, 3).join(' · ') + (o.reason ? ` — ${o.reason}` : ''),
        score: 55,
      });
    });

    // 5. Lost-Lead reminders — checkpoints within 30 days show up. Closer = louder.
    lostLeads.forEach((ll) => {
      const cp = ll.next_checkpoint;
      if (!cp || cp.days_until == null || cp.days_until > 30) return;
      const score = 45 - Math.min(cp.days_until, 30) * 1; // 0d → 45, 30d → 15
      feed.push({
        kind: 'winback',
        id: ll.id,
        title: `Win-back reminder · ${ll.name}`,
        subtitle: `${ll.what_lost} · checkpoint in ${cp.days_until}d (${cp.offset}d before anniversary)`,
        value: ll.premium || null,
        score,
      });
    });

    return feed.sort((a, b) => b.score - a.score);
  }, [briefing, narrative, dailyQueue, renewals90, opportunities, lostLeads]);

  // Right-sidebar target: pace vs YTD average monthly commission.
  const targetPct = useMemo(() => {
    const monthly = stats?.commission_earned_monthly || 0;
    const yearlyAvg = (stats?.commission_earned_yearly || 0) / 12;
    if (yearlyAvg <= 0) return null;
    return Math.round((monthly / yearlyAvg) * 100);
  }, [stats]);

  // Top 3 loss reasons for the current calendar month — surfaces price / competitor
  // spikes early. Bucketing anything without a reason under "Unspecified" so agents
  // see coverage of the tagging habit too.
  const topLossReasonsMTD = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth();
    const counts = new Map();
    let scanned = 0;
    for (const ll of lostLeads) {
      if (!ll.lost_at) continue;
      const d = new Date(ll.lost_at);
      if (d.getFullYear() !== y || d.getMonth() !== m) continue;
      scanned += 1;
      const key = (ll.loss_reason && ll.loss_reason.trim()) || 'Unspecified';
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 3);
    return { total: scanned, rows: sorted.map(([key, count]) => ({ key, count })) };
  }, [lostLeads]);

  // Renewal calendar side-panel selection — {key, list}. Auto-selects the  // first due day in the visible month so the panel is never empty on load.
  const [calSelection, setCalSelection] = useState(null);
  useEffect(() => {
    if (calSelection || renewals90.length === 0) return;
    const firstDue = renewals90
      .filter((r) => r.next_due_date)
      .sort((a, b) => new Date(a.next_due_date) - new Date(b.next_due_date))[0];
    if (!firstDue) return;
    const d = new Date(firstDue.next_due_date);
    const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const list = renewals90.filter((r) => {
      if (!r.next_due_date) return false;
      const dd = new Date(r.next_due_date);
      return `${dd.getFullYear()}-${dd.getMonth()}-${dd.getDate()}` === k;
    });
    setCalSelection({ key: k, list });
  }, [renewals90, calSelection]);



  // Friendly empty-state — shown to brand-new agents and right after a "Clear All My
  // Data" purge, so nobody lands on an empty stats grid wondering what to do next.
  const isEmpty = (stats?.total_clients || 0) === 0 && (stats?.active_policies || 0) === 0;
  if (isEmpty) {
    return (
      <div className="space-y-8" data-testid="dashboard-empty">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''} — your CRM is a clean slate
          </h1>
          <p className="text-slate-600 mt-2 text-sm max-w-2xl">
            No clients or products yet. Pick a starting point below — you can always change your mind.
            Everything here (renewals, commissions, AI briefings) lights up automatically once you add data.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            type="button"
            onClick={() => document.querySelector('[data-testid="topbar-add-client"]')?.click()}
            className="text-left rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/40 hover:bg-blue-50 hover:border-blue-400 transition p-5"
            data-testid="empty-add-client"
          >
            <div className="flex items-center gap-2 text-blue-800 mb-2">
              <Users className="h-5 w-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">1 · Add your first client</span>
            </div>
            <p className="text-sm text-slate-700">Enter one client manually — name, mobile, PAN. Takes about 30 seconds.</p>
            <p className="text-xs text-blue-700 mt-3 font-medium">Open Add Client form →</p>
          </button>

          <button
            type="button"
            onClick={() => runQuickAction({ label: 'Health', productType: 'Health' })}
            className="text-left rounded-xl border-2 border-dashed border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50 hover:border-emerald-400 transition p-5"
            data-testid="empty-add-product"
          >
            <div className="flex items-center gap-2 text-emerald-800 mb-2">
              <FileText className="h-5 w-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">2 · Add a product / policy</span>
            </div>
            <p className="text-sm text-slate-700">Link an insurance policy, MF, loan, or FD to a client to start tracking premium and renewals.</p>
            <p className="text-xs text-emerald-700 mt-3 font-medium">Open Add Product picker →</p>
          </button>

          <button
            type="button"
            onClick={() => window.location.href = '/clients'}
            className="text-left rounded-xl border-2 border-dashed border-amber-200 bg-amber-50/40 hover:bg-amber-50 hover:border-amber-400 transition p-5"
            data-testid="empty-bulk-upload"
          >
            <div className="flex items-center gap-2 text-amber-800 mb-2">
              <BarChart3 className="h-5 w-5" />
              <span className="text-sm font-semibold uppercase tracking-wider">3 · Bulk upload from Excel</span>
            </div>
            <p className="text-sm text-slate-700">Have an existing spreadsheet? Import 500 clients or products at once from Clients → Bulk Upload.</p>
            <p className="text-xs text-amber-700 mt-3 font-medium">Go to Clients →</p>
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-semibold">What lights up next</p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-700">
            <li className="flex items-center gap-2"><Calendar className="h-4 w-4 text-slate-400" /> Renewal calendar with 7/15/30/60/90 day tiles</li>
            <li className="flex items-center gap-2"><Lightbulb className="h-4 w-4 text-slate-400" /> AI-powered daily briefing and cross-sell suggestions</li>
            <li className="flex items-center gap-2"><Target className="h-4 w-4 text-slate-400" /> Action plan ranked by revenue impact</li>
            <li className="flex items-center gap-2"><FileText className="h-4 w-4 text-slate-400" /> Weekly digest emailed to you every Sunday morning</li>
          </ul>
        </div>
      </div>
    );
  }

  const pendingRenewals = renewals90.filter(r => r.days_remaining <= 30);
  const glanceMetrics = [
    { label: 'Total Clients',        value: stats?.total_clients || 0,                                 tint: 'blue' },
    { label: 'Active Policies',      value: stats?.active_policies || 0,                               tint: 'green' },
    { label: 'Expiring (30d)',       value: stats?.expiring_30_days || 0,                              tint: 'orange' },
    { label: 'Premium This Month',   value: formatCurrency(stats?.premium_collected_month || 0),       tint: 'blue' },
  ];

  return (
    <div className="space-y-6 relative" data-testid="dashboard">
      <FloatingSymbols opacity={0.7} className="absolute inset-0 -top-6 -left-6 -right-6 -z-0 pointer-events-none" />
      <div className="relative z-10 space-y-6">
      {/* Header row: greeting + optional cross-sell + AI dock */}
      {/* Greeting is OUTSIDE the AI row so it can't offset the first left Panel
          from the AI panel's top edge. Row below contains only the two blocks
          that must share top/bottom alignment. */}
      <div className="flex items-center gap-4">
        {user?.profile_photo && (
          <img src={user.profile_photo.startsWith('http') ? user.profile_photo : `${process.env.REACT_APP_BACKEND_URL}${user.profile_photo}`} alt={user?.name} className="h-14 w-14 lg:h-16 lg:w-16 rounded-full object-cover ring-2 ring-white shadow-sm shrink-0" data-testid="dashboard-agent-photo" />
        )}
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Welcome back, {user?.name}
          </h1>
          <p className="text-slate-600 mt-1 text-sm">Here's what's happening with your business today</p>
        </div>
      </div>

      <div className={`flex flex-col lg:flex-row lg:gap-4 ${aiPanelExpanded ? 'lg:items-stretch' : 'lg:items-start'}`}>
        <div className={`min-w-0 space-y-4 ${aiPanelExpanded ? 'lg:flex-1 lg:flex lg:flex-col' : 'flex-1'}`}>

          {/* Vertical stack of Cross-Sell + Action Plan, only in AI expanded mode.
              flex-1 + flex-col lets the two stacked cards fill the row's stretched
              height so the AI Briefing panel's bottom edge aligns with the second
              card's bottom edge — regardless of how much content each holds. */}
          {aiPanelExpanded && (
            <div className="lg:flex-1 lg:flex lg:flex-col lg:gap-3 space-y-3 lg:space-y-0" data-testid="ai-expanded-stack">
              <div className="lg:flex-1 lg:min-h-0 lg:flex lg:flex-col">
              <Panel
                title="Cross-Sell Opportunities"
                icon={Lightbulb}
                iconTint="text-purple-600"
                description="Identified gaps in client coverage"
                testId="opportunities-inline"
                className="lg:flex-1 lg:min-h-0"
              >
                {opportunities.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No opportunities detected yet</p>
                ) : (
                  <ul className="space-y-2 flex-1 min-h-0 overflow-y-auto">
                    {opportunities.slice(0, 4).map((opp) => (
                      <li key={opp.client_id} className="py-2 border-b border-slate-100 last:border-0">
                        <p className="text-sm font-medium text-slate-900">{opp.client_name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{opp.reason}</p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {opp.recommended_products.map((product) => (
                            <Badge key={product} variant="secondary" className="bg-purple-100 text-purple-800 text-[10px]">{product}</Badge>
                          ))}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
              </div>
              <div className="lg:flex-1 lg:min-h-0 lg:flex lg:flex-col">
              <Panel
                title="Today's Queue"
                icon={Target}
                iconTint="text-blue-800"
                description="AI-suggested actions + your manual tasks — one prioritized list"
                testId="daily-queue-inline"
                className="lg:flex-1 lg:min-h-0"
              >
                <DailyQueue queue={dailyQueue} onComplete={handleQueueComplete} completingId={completingId} compact />
              </Panel>
              </div>
            </div>
          )}
        </div>
        <AIBriefingPanel
          narrative={narrative}
          narrativeLoading={narrativeLoading}
          narrativeRefreshing={narrativeRefreshing}
          onRefreshNarrative={loadNarrative}
          weeklyWrap={weeklyWrap}
          weeklyLoading={weeklyLoading}
          expanded={aiPanelExpanded}
          onExpandedChange={setAiPanelExpanded}
        />
      </div>

      {/* Claims Needing Attention — standalone KPI tile (Round 62).
          Hidden when zero so the dashboard stays clean. */}
      {claimsAttention > 0 && (
        <button
          type="button"
          onClick={() => { window.location.href = '/claims?attention=1'; }}
          className="w-full rounded-xl border border-red-200 bg-red-50/70 hover:bg-red-50 transition p-4 flex items-center justify-between gap-4 text-left"
          data-testid="dashboard-claims-attention-card"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center text-red-700">
              <FileWarning className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-red-700">Claims Need Attention</p>
              <p className="text-2xl font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope' }}>
                {claimsAttention} <span className="text-sm font-medium text-slate-600">claim{claimsAttention === 1 ? '' : 's'} with follow-up due today or overdue</span>
              </p>
            </div>
          </div>
          <ArrowRight className="h-5 w-5 text-red-700" />
        </button>
      )}

      {/* Business at a Glance — collapsible key metrics */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="business-at-a-glance">
        <button
          type="button"
          onClick={() => setGlanceOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-left"
          data-testid="business-at-a-glance-toggle"
        >
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-blue-800" />
            <span className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Business at a Glance</span>
            <span className="text-xs text-slate-500">— key metrics</span>
          </div>
          {glanceOpen ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
        </button>
        {glanceOpen && (
          <div className="px-4 pb-4 pt-0 grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }} data-testid="business-glance-metrics">
            {glanceMetrics.map((m) => <Metric key={m.label} label={m.label} value={m.value} tint={m.tint} />)}
          </div>
        )}
      </div>

      {/* Quick Actions — always require client/lead selection first */}
      <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="quick-actions">
        <div className="flex items-center justify-between mb-2 px-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Quick Actions</p>
          <p className="text-[11px] text-slate-400">Pick a client or lead first, then choose the product</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {QUICK_ACTIONS.map(({ label, productType, icon: Icon, accent }) => (
            <button
              key={label}
              type="button"
              onClick={() => runQuickAction({ label, productType })}
              className="group flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-white hover:border-blue-300 hover:shadow-sm transition-all text-left"
              data-testid={`quick-action-${label.toLowerCase()}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Icon className={`h-4 w-4 ${accent}`} />
                <span className="text-sm font-medium text-slate-900 truncate">{label}</span>
              </div>
              <Plus className="h-4 w-4 text-slate-400 group-hover:text-blue-700 shrink-0" />
            </button>
          ))}
          <button
            type="button"
            onClick={() => openAddProduct()}
            className="group flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-dashed border-slate-300 text-slate-600 hover:border-blue-400 hover:text-blue-800 hover:bg-blue-50 transition-all"
            data-testid="quick-action-more"
          >
            <span className="text-sm font-medium">More</span>
            <Plus className="h-4 w-4 shrink-0" />
          </button>
        </div>
      </div>

      {/* Priority Feed (left) + Compact Stats sidebar (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="priority-feed-layout">
        {/* LEFT — the unified feed */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="priority-feed">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-blue-800" />
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Your Priority Feed</h2>
              </div>
              <p className="text-[11px] text-slate-500">Briefing · Queue · Renewals · Cross-sell · Win-back — ranked</p>
            </div>
            {priorityFeed.length === 0 ? (
              <div className="text-center py-10 text-slate-500" data-testid="feed-empty">
                <Sparkles className="h-6 w-6 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">You're all caught up. Add a client or product to seed new items.</p>
              </div>
            ) : (
              <ul className="space-y-2 max-h-[640px] overflow-y-auto pr-1" data-testid="priority-feed-list">
                {priorityFeed.map((item) => {
                  const key = `${item.kind}-${item.id}`;
                  return (
                    <FeedRow
                      key={key}
                      item={item}
                      busy={feedBusyId === key}
                      onCall={handleFeedCall}
                      onWhatsApp={handleFeedWhatsApp}
                      onComplete={handleFeedComplete}
                    />
                  );
                })}
              </ul>
            )}
          </div>

          {/* Skip Today — lower visual weight, sits directly below the priority feed.
              Uses the same underlying signals but surfaces the inverse: who NOT to
              call today and why. Muted greys + slim borders keep it visually
              subordinate to the priority feed above. */}
          <SkipTodayPanel data={skipToday} loading={skipTodayLoading} onSnoozed={loadDashboardData} />
        </div>

        {/* RIGHT — compact stats sidebar */}
        <div className="space-y-4">
          {/* Business Health — heuristic composite score with top-driver call-out */}
          <HealthScoreCard data={healthScore} loading={healthScoreLoading} trend={healthTrend} />

          {/* Tip of the Day — founder-authored coaching content, rotates daily */}
          <TipOfTheDayCard />

          {/* Target — pace vs YTD monthly avg */}
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="sidebar-target-card">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-blue-800" />
              <p className="text-sm font-semibold text-slate-900">Target</p>
            </div>
            {targetPct == null ? (
              <p className="text-xs text-slate-500 py-2">Not enough YTD data yet — earn commission across a full month to see pace.</p>
            ) : (
              <>
                <div className="flex items-baseline gap-2">
                  <span className={`text-3xl font-bold ${targetPct >= 100 ? 'text-emerald-700' : targetPct >= 75 ? 'text-blue-800' : 'text-amber-700'}`} style={{ fontFamily: 'Manrope' }}>{targetPct}%</span>
                  <span className="text-xs text-slate-500">of YTD monthly avg</span>
                </div>
                <div className="mt-2 h-1.5 bg-slate-100 rounded overflow-hidden">
                  <div className={`h-full ${targetPct >= 100 ? 'bg-emerald-600' : targetPct >= 75 ? 'bg-blue-700' : 'bg-amber-500'}`} style={{ width: `${Math.min(targetPct, 100)}%` }} />
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  {formatCurrency(stats?.commission_earned_monthly || 0)} this month vs {formatCurrency((stats?.commission_earned_yearly || 0) / 12)} avg
                </p>
              </>
            )}
          </div>

          {/* This month */}
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="sidebar-this-month-card">
            <p className="text-sm font-semibold text-slate-900 mb-2">This Month</p>
            <div className="space-y-2">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500">Premium collected</p>
                <p className="text-2xl font-bold text-blue-800" style={{ fontFamily: 'Manrope' }}>{formatCurrency(stats?.premium_collected_month || 0)}</p>
              </div>
              <div className="pt-2 border-t border-slate-100">
                <p className="text-[11px] uppercase tracking-wider text-slate-500">Renewals due</p>
                <p className="text-2xl font-bold text-amber-700" style={{ fontFamily: 'Manrope' }}>{stats?.pending_renewals || 0}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">{stats?.expiring_30_days || 0} in next 30 days</p>
              </div>
            </div>
          </div>

          {/* Top Loss Reasons — MTD.  Surfaces price / competitor spikes early. */}
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="sidebar-loss-reasons-card">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-slate-900">Top loss reasons</p>
              <span className="text-[10px] uppercase tracking-wider text-slate-400">this month</span>
            </div>
            {topLossReasonsMTD.total === 0 ? (
              <p className="text-xs text-slate-500 py-2" data-testid="loss-reasons-empty">No lost records yet this month — keep it that way.</p>
            ) : (
              <>
                <ul className="space-y-1.5" data-testid="loss-reasons-list">
                  {topLossReasonsMTD.rows.map((r, i) => {
                    const pct = Math.round((r.count / topLossReasonsMTD.total) * 100);
                    const barColor = i === 0 ? 'bg-red-500' : i === 1 ? 'bg-amber-500' : 'bg-slate-400';
                    return (
                      <li key={r.key} data-testid={`loss-reason-${r.key.toLowerCase().replace(/\s+/g, '-')}`}>
                        <div className="flex items-center justify-between text-[11px] mb-0.5">
                          <span className="text-slate-800 font-medium truncate mr-2">{r.key}</span>
                          <span className="text-slate-500 shrink-0">{r.count} · {pct}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
                          <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <p className="text-[10px] text-slate-500 mt-2">{topLossReasonsMTD.total} lost this month · <button type="button" onClick={() => window.location.href = '/reports'} className="text-blue-700 hover:underline" data-testid="loss-reasons-see-more">see all</button></p>
              </>
            )}
          </div>


          {/* Product Mix — donut */}
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="sidebar-product-mix-card">
            <p className="text-sm font-semibold text-slate-900 mb-2">Product Mix</p>            {productMix.length === 0 ? (
              <p className="text-xs text-slate-500 py-2">No products yet — add one to see the mix.</p>
            ) : (
              <>
                <div style={{ width: '100%', height: 140 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={productMix.map((c) => ({ name: (c.key || '').replace(/_/g, ' '), value: c.records }))}
                        dataKey="value"
                        innerRadius={35}
                        outerRadius={60}
                        paddingAngle={2}
                      >
                        {productMix.map((_, i) => (
                          <Cell key={i} fill={['#1E40AF', '#0EA5E9', '#7C3AED', '#F59E0B', '#EF4444', '#10B981'][i % 6]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="mt-2 space-y-1" data-testid="product-mix-legend">
                  {productMix.slice(0, 6).map((c, i) => (
                    <li key={c.key} className="flex items-center gap-2 text-[11px] text-slate-600">
                      <span className="h-2 w-2 rounded-sm shrink-0" style={{ background: ['#1E40AF', '#0EA5E9', '#7C3AED', '#F59E0B', '#EF4444', '#10B981'][i % 6] }} />
                      <span className="flex-1 capitalize truncate">{(c.key || '').replace(/_/g, ' ')}</span>
                      <span className="font-medium text-slate-800">{c.records}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Renewal Calendar — full-width row below priority feed */}
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="renewal-calendar-row">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-indigo-700" />
            <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Renewal Calendar</h2>
          </div>
          <p className="text-[11px] text-slate-500">Amber dots = renewals due. Click a day to see who and send a one-tap reminder.</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <RenewalCalendar
              renewals={renewals90}
              selectedKey={calSelection?.key}
              onDaySelect={(key, list) => setCalSelection({ key, list })}
            />
          </div>
          <div className="lg:border-l lg:border-slate-100 lg:pl-4" data-testid="renewal-calendar-side-panel">
            {!calSelection ? (
              <div className="text-center py-10 text-slate-500">
                <Calendar className="h-6 w-6 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Pick a highlighted day on the left to see its renewals.</p>
              </div>
            ) : (() => {
              const [yy, mm, dd] = calSelection.key.split('-').map(Number);
              const heading = new Date(yy, mm, dd).toDateString();
              return (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-slate-900">
                      {heading}
                      <span className="ml-2 text-xs font-normal text-slate-500">
                        {calSelection.list.length} renewal{calSelection.list.length === 1 ? '' : 's'} due
                      </span>
                    </p>
                  </div>
                  {calSelection.list.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-6">No renewals on this day.</p>
                  ) : (
                    <ul className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="renewal-day-list">
                      {calSelection.list.map((r) => {
                        const smsBusy = calSendingId === `${r.policy_id}-sms`;
                        const waBusy = calSendingId === `${r.policy_id}-whatsapp`;
                        const noPhone = !r.client_phone;
                        return (
                        <li key={r.policy_id} className="rounded-md border border-slate-200 bg-white p-3 space-y-2" data-testid={`renewal-day-item-${r.policy_id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{r.client_name || 'Client'}</p>
                              <p className="text-xs text-slate-500 truncate">
                                {r.policy_number} · {r.product_type || ''}
                              </p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-sm font-semibold text-emerald-700">{formatCurrency(r.premium_amount)}</p>
                              <p className="text-[11px] text-slate-500">{r.days_remaining != null ? (r.days_remaining === 0 ? 'Due today' : `in ${r.days_remaining}d`) : '—'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => sendRenewalReminder(r, 'sms')}
                              disabled={smsBusy || noPhone}
                              className="flex-1 inline-flex items-center justify-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                              data-testid={`calendar-send-sms-${r.policy_id}`}
                              title={noPhone ? 'No mobile on file' : 'Send SMS reminder'}
                            >
                              <Phone className="h-3 w-3" />
                              {smsBusy ? 'Sending…' : 'SMS'}
                            </button>
                            <button
                              type="button"
                              onClick={() => sendRenewalReminder(r, 'whatsapp')}
                              disabled={waBusy || noPhone}
                              className="flex-1 inline-flex items-center justify-center gap-1 rounded border border-green-300 bg-green-50 px-2 py-1 text-[11px] font-medium text-green-800 hover:bg-green-100 disabled:opacity-50"
                              data-testid={`calendar-send-wa-${r.policy_id}`}
                              title={noPhone ? 'No mobile on file' : 'Send WhatsApp reminder'}
                            >
                              <MessageSquare className="h-3 w-3" />
                              {waBusy ? 'Sending…' : 'WhatsApp'}
                            </button>
                          </div>
                          {noPhone && (
                            <p className="text-[10px] text-amber-700" data-testid={`calendar-no-phone-${r.policy_id}`}>
                              No mobile on file — add one from the client's record to enable one-tap send.
                            </p>
                          )}
                        </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Attach product to Lead — captures product interest without forcing client conversion */}
      <Dialog open={!!leadAttach} onOpenChange={(o) => { if (!o) setLeadAttach(null); }}>
        <DialogContent className="max-w-md" data-testid="lead-attach-dialog">
          <DialogHeader>
            <DialogTitle>Attach {leadAttach?.label || 'product'} interest to lead</DialogTitle>
            <DialogDescription>
              Recording <strong>{leadAttach?.name}</strong>'s interest on the lead. The full policy will be created automatically when you convert them into a client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Product Type</Label>
              <Input value={leadAttach?.productType || ''} disabled className="bg-slate-50" data-testid="lead-attach-product-type" />
              <p className="text-[10px] text-slate-500 mt-1">Fixed by the Quick Action tile.</p>
            </div>
            <div>
              <Label className="text-xs">Product Name</Label>
              <Input value={leadAttachForm.product} onChange={(e) => setLeadAttachForm({ ...leadAttachForm, product: e.target.value })}
                disabled={!leadAttach?.productType}
                placeholder={leadAttach?.productType ? '' : 'Select Product Type first'}
                data-testid="lead-attach-product" />
            </div>
            <div>
              <Label className="text-xs">Expected Premium (₹)</Label>
              <Input type="number" min="0" placeholder="Leave blank if unknown" value={leadAttachForm.expected_premium} onChange={(e) => setLeadAttachForm({ ...leadAttachForm, expected_premium: e.target.value })} data-testid="lead-attach-premium" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Input placeholder="Quote sent, waiting on KYC, etc." value={leadAttachForm.notes} onChange={(e) => setLeadAttachForm({ ...leadAttachForm, notes: e.target.value })} data-testid="lead-attach-notes" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setLeadAttach(null)}>Cancel</Button>
              <Button className="bg-blue-800 hover:bg-blue-900" disabled={leadAttachSaving} onClick={saveLeadAttach} data-testid="lead-attach-save">
                {leadAttachSaving ? 'Saving…' : 'Attach to lead'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default Dashboard;
